import React, {Fragment, useCallback, useRef} from 'react';
import cn from 'classnames';
import {createUseStyles} from 'react-jss';
import UIIcon from '~/old-ui/UIIcon';
import {isBrand, isCompany, isTrainer} from '~/types/account';
import {brandColor, defaultFontSize, mediaSizes, primaryTextColor, secondaryTextColor, smallFontSize} from '~/theme';
import AccountAvatar from '~/components/AccountAvatar';
import {externalUrl} from '~/utils';
import IconExternalLink from '../icon/IconExternalLink';
import {Popover, Transition} from '@headlessui/react';

export default function NavbarAccountMenuItem({
                                                user, account, className, ...props
                                              }) {
  const classes = useStyles();

  const logoutFormRef = useRef();
  const onLogoutClick = useCallback(() => {
    logoutFormRef.current.submit();
  }, [logoutFormRef]);

  return (
    <>
      <Popover className="tailwind relative z-50">
        <>
          <Popover.Button className={cn(classes.root, className)}>
            <div className={classes.accountAvatarUserInfo}>
              <AccountAvatar
                className={classes.avatar}
                account={account}
              />
              <div className={classes.userInfo}>
                <div className={classes.userShortName}>
                  {user.firstName}
                </div>
                <div className={classes.userRole}>
                  {account.name}
                </div>
              </div>
            </div>

            <UIIcon name="fa-caret-down" className={classes.caretDown}/>
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              className="absolute z-10 w-72 p-4 mt-3 transform -translate-x-1/2 left-20
                bg-white rounded-md shadow-xl ring-2 ring-gray ring-opacity-20 overflow-hidden ">
              <>
                {user.accounts.length > 1 &&
                  <>
                    <div className={classes.accountDropDownMenuTitle}>Switch account</div>

                    {user.accounts.sort((left, right) => left.name.localeCompare(right.name)).map((userAccount) => (
                      <NavbarDropdownAccountItem
                        key={userAccount.id}
                        account={userAccount}
                        active={(account.id === userAccount.id)}
                      />
                    ))}

                    <hr className={classes.hr}/>
                  </>
                }
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a href="https://support.workshopbutler.com" target="_blank"
                   className={classes.accountDropDownMenuLink}>
                  Help Center <IconExternalLink className="inline-block"/></a>
                <hr className={classes.hr}/>

                {isBrand(account) && (
                  <a href={externalUrl('/brand/settings')} className={classes.accountDropDownMenuLink}>Account
                    settings</a>
                )}
                {isCompany(account) && (
                  <a href={externalUrl('/company/settings')} className={classes.accountDropDownMenuLink}>Account
                    settings</a>
                )}
                {isTrainer(account) && (
                  <a href={externalUrl('/trainer/settings')} className={classes.accountDropDownMenuLink}>Account
                    settings</a>
                )}

                <a href={externalUrl('/user/settings')} className={classes.accountDropDownMenuLink}>Login settings</a>
                <a href="#" className={classes.accountDropDownMenuLink} onClick={onLogoutClick}>Log out</a>

                <form action={externalUrl('/v2/logout')} method="POST" ref={logoutFormRef}>
                  <input type="hidden" name="logout" value="1"/>
                </form>
              </>
            </Popover.Panel>
          </Transition>
        </>
      </Popover>
    </>
  );
}

function NavbarDropdownAccountItem({account, active}) {
  const classes = useStyles();

  const children = (
    <>
      <AccountAvatar
        className={classes.userAccountImage}
        account={account}
      />

      <span className={classes.userAccountName}>{account.name}</span>
    </>
  );


  return active ? (
    <div className={cn(classes.userAccount, classes.userAccountSelected)}>{children}</div>
  ) : (
    <a className={classes.userAccount} href={externalUrl(`/account/switch/${account.id}`)}>{children}</a>
  );
}

const useStyles = createUseStyles({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer',
    textDecoration: 'none',
    width: 180,
    padding: [[0, 0, 0, 6]],
    boxSizing: 'content-box',
    height: '100%',
    alignItems: 'center',
    transition: [['background-color', '100ms', 'linear']],

    [mediaSizes.md]: {
      width: 60,
    },
  },

  avatar: {
    width: 35,
    height: 35,
    minWidth: 35,
    background: '#eee',
  },

  userInfo: {
    textAlign: 'left',
    fontSize: defaultFontSize,
    lineHeight: '16px',
    color: '#fff',
    padding: [[1, 0, 1, 12]],
    overflow: 'hidden',

    [mediaSizes.md]: {
      display: 'none',
    },
  },

  userShortName: {
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  userRole: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
  },

  caretDown: {
    color: '#fff',
    paddingLeft: 12,
    boxSizing: 'content-box',
  },

  accountDropDownMenu: {
    marginTop: 8,
    padding: 16,
    boxSizing: 'border-box',
    borderRadius: 4,
    boxShadow: [[0, 0, 4, 'rgba(0, 0, 0, 0.2)']],
    border: [[1, 'solid', '#F6F6F6']],
    minWidth: 280,
    maxWidth: 300,
    fontSize: defaultFontSize,
  },

  accountAvatarUserInfo: {
    display: 'flex',
    width: 146,
  },

  hr: {
    border: 0,
    borderBottom: [[1, 'solid', '#E7E7E7']],
    margin: [[16, 0]],
  },

  accountDropDownMenuTitle: {
    color: secondaryTextColor,
    fontSize: smallFontSize,
    textTransform: 'uppercase',
    paddingLeft: 2,
    marginBottom: 16,
  },

  accountDropDownMenuLink: {
    display: 'block',
    color: brandColor,
    textDecoration: 'none',
    transition: [['all', '0.15s', 'ease']],

    '& + &': {
      marginTop: 12,
    },

    '&:hover, &:focus': {
      textDecoration: 'none',
    },
  },

  userAccount: {
    color: brandColor,
    textDecoration: 'none',
    height: 24,
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',

    '&:hover, &:focus': {
      textDecoration: 'none',
    },
  },

  userAccountImage: {
    userSelect: 'none',
    width: 24,
    height: 24,
    minWidth: 24,
    marginRight: 10,
  },

  userAccountName: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },

  userAccountSelected: {
    color: primaryTextColor,
  },
});
